<template>
  <component
    :is="iconComponent"
    :size="size"
    :stroke-width="strokeWidth"
    :class="colorClass"
  />
</template>

<script setup>
import { computed } from 'vue';
import {
  Flame,
  PartyPopper,
  Star,
  Sprout,
  BookOpen,
  Rocket,
  Crown,
  PlayCircle,
  Play,
  Award,
  RefreshCw,
  FlaskConical,
  Clock,
  CheckCircle,
  Check,
  Trophy,
  Target,
  Zap,
  TrendingUp,
  Calendar,
  BarChart3,
  Sparkles,
  Medal,
  GraduationCap,
  Brain,
  Lightbulb,
  CircleCheck,
  Circle,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ArrowRight,
  ArrowDown,
  Home,
  BookMarked,
  ClipboardList,
  Settings,
  User,
  LogOut,
  Video,
  Image,
  ListChecks,
  Calculator,
  Loader,
  AlertCircle,
  XCircle,
  Map,
  Lock,
  Route,
  LayoutGrid,
  Mountain,
  FileText,
  Info,
  Layers,
  HelpCircle,
  Coffee,
  X,
  Download,
} from 'lucide-vue-next';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  size: {
    type: [Number, String],
    default: 20,
  },
  strokeWidth: {
    type: [Number, String],
    default: 2,
  },
  color: {
    type: String,
    default: null, // null = inherit current text color
  },
});

// Map icon names to components
const iconMap = {
  // Gamification
  flame: Flame,
  'party-popper': PartyPopper,
  star: Star,
  sprout: Sprout,
  'book-open': BookOpen,
  rocket: Rocket,
  crown: Crown,
  trophy: Trophy,
  award: Award,
  medal: Medal,
  sparkles: Sparkles,
  zap: Zap,
  target: Target,

  // Actions
  'play-circle': PlayCircle,
  play: Play,
  'refresh-cw': RefreshCw,
  'flask-conical': FlaskConical,
  clock: Clock,
  'check-circle': CheckCircle,
  check: Check,
  'circle-check': CircleCheck,
  circle: Circle,
  'alert-circle': AlertCircle,
  'x-circle': XCircle,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'arrow-right': ArrowRight,
  'arrow-down': ArrowDown,
  loader: Loader,
  lock: Lock,
  x: X,
  download: Download,
  info: Info,
  coffee: Coffee,

  // Media
  video: Video,
  image: Image,
  'list-checks': ListChecks,
  calculator: Calculator,
  'file-text': FileText,
  layers: Layers,
  'help-circle': HelpCircle,

  // Progress
  'trending-up': TrendingUp,
  'bar-chart': BarChart3,
  calendar: Calendar,

  // Learning
  'graduation-cap': GraduationCap,
  brain: Brain,
  lightbulb: Lightbulb,
  'book-marked': BookMarked,
  'clipboard-list': ClipboardList,

  // Navigation
  home: Home,
  settings: Settings,
  user: User,
  'log-out': LogOut,
  map: Map,
  route: Route,
  'layout-grid': LayoutGrid,
  mountain: Mountain,
};

const iconComponent = computed(() => {
  return iconMap[props.name] || Circle;
});

const colorClass = computed(() => {
  if (!props.color) return '';

  const colorMap = {
    // Brand colors
    primary: 'text-blue-600',
    secondary: 'text-teal-600',
    success: 'text-green-500',
    warning: 'text-amber-500',
    danger: 'text-red-500',

    // Specific use cases
    flame: 'text-orange-500',
    gold: 'text-yellow-500',
    purple: 'text-purple-500',
    muted: 'text-gray-400',
  };

  return colorMap[props.color] || '';
});
</script>
